import React, { useEffect, useRef } from 'react';

import { node, bool, func } from 'prop-types';

export const ARROW_KEYS_NAVIGATION_ITEM_CLASS = 'arrows-navigation-item';

/**
 *
 * onClose - return true to prevent default browser behavior
 */
export const useArrowKeysNavigation = ({
  containerRef,
  onClose,
  autoFocusOnOpen,
}) => {
  const ref = containerRef;
  const focusedIndex = useRef(0);

  const getItems = () =>
    ref.current?.querySelectorAll(`.${ARROW_KEYS_NAVIGATION_ITEM_CLASS}`) || [];

  const jumpToBottom = () => {
    const items = getItems();

    if (focusedIndex.current === items.length - 1) {
      return;
    }

    focusedIndex.current = items.length - 1;
    items[focusedIndex.current]?.focus();
  };

  const jumpToTop = () => {
    const items = getItems();

    if (focusedIndex.current === 0) {
      return;
    }

    focusedIndex.current = 0;
    items[focusedIndex.current]?.focus();
  };

  const scrollIntoView = () => {
    const items = getItems();
    const element = items[focusedIndex.current];

    if (!element) {
      return true;
    }

    element.focus();

    if (element.scrollIntoViewIfNeeded) {
      element.scrollIntoViewIfNeeded();
    } else {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }

    return false;
  };

  const moveDown = () => {
    const items = getItems();

    focusedIndex.current =
      focusedIndex.current === items.length - 1 ? 0 : focusedIndex.current + 1;

    return scrollIntoView();
  };

  const moveUp = () => {
    const items = getItems();

    focusedIndex.current =
      focusedIndex.current === 0 ? items.length - 1 : focusedIndex.current - 1;

    return scrollIntoView();
  };

  // Return false to prevent default browser behavior
  const navigate = event => {
    const { key, shiftKey } = event;

    if (!ref.current) {
      return true;
    }

    if (key === 'Tab') {
      if (shiftKey) {
        jumpToTop();
      } else {
        jumpToBottom();
      }

      if (onClose && onClose()) {
        return false;
      }

      return true;
    }

    if (key === 'ArrowDown') {
      event.stopPropagation();
      return moveDown();
    }

    if (key === 'ArrowUp') {
      event.stopPropagation();
      return moveUp();
    }

    return true;
  };

  const startArrowsNavigation = () => {
    ref.current?.addEventListener('keydown', navigate);

    if (autoFocusOnOpen) {
      scrollIntoView();
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ref.current?.removeEventListener('keydown', navigate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(
    startArrowsNavigation,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoFocusOnOpen, ref.current],
  );

  return { startArrowsNavigation };
};

const ArrowKeysNavigation = ({
  children,
  onClose,
  autoFocusOnOpen = false,
  component = 'div',
  ...rest
}) => {
  const ref = useRef(null);

  useArrowKeysNavigation({ containerRef: ref, autoFocusOnOpen, onClose });

  return React.createElement(
    component,
    { ref, ...rest },
    <div tabIndex={-1}>{children}</div>,
  );
};

ArrowKeysNavigation.propTypes = {
  onClose: func,
  autoFocusOnOpen: bool,
  children: node.isRequired,
  component: node,
};

ArrowKeysNavigation.defaultProps = {
  onClose: null,
  autoFocusOnOpen: false,
  component: 'div',
};

export default ArrowKeysNavigation;
